import React, { Component } from 'react';
import {
    DndContext,
    closestCenter,
    PointerSensor,
    useSensor,
    useSensors,
} from '@dnd-kit/core';
import {
    SortableContext,
    arrayMove,
    rectSortingStrategy,
    useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { connect } from 'react-redux';
import { fetchPublicacion, editPublicacion, setImagesUploader } from '../../../../actions/publicacionesActions';
import { toast } from '../../../../utilities/appToast';
import { esImagenSinProcesar, ordenarImagenes, prepararImagenes } from '../../../../utilities/publicacionesSinImagen';
import PropTypes from 'prop-types';

const SortableImage = ({ id, image, pendiente, onDelete }) => {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        margin: '10px',
        opacity: isDragging ? 0.5 : 1,
        cursor: 'grab',
    };

    return (
        <div ref={setNodeRef} className="publicacion-sortable-image" style={style} {...attributes} {...listeners}>
            <div className="publicacion-sortable-image__frame" style={{ margin: '10px' }}>
                {pendiente && <span className="publicacion-sortable-image__pending">Sin procesar</span>}
                <img
                    src={image.urlImagen}
                    alt={image.name || (pendiente ? 'Imagen sin procesar' : 'Imagen de la publicación')}
                    className="publicacion-sortable-image__preview"
                    style={{
                        width: '100%',
                        maxWidth: '250px',
                        height: 'auto',
                        maxHeight: '250px',
                        objectFit: 'contain',
                        marginRight: '10px',
                    }}
                />
            </div>
            <div style={{ display: 'flex', justifyContent: 'center' }}>
                <button
                    type="button"
                    className="btn btn-light"
                    onPointerDown={(e) => e.stopPropagation()}
                    onClick={(e) => {
                        e.preventDefault();
                        onDelete();
                    }}
                    style={{ width: '40px', height: '40px' }}
                >
                    <i className="fas fa-trash-alt" style={{ pointerEvents: 'none' }} />
                </button>
            </div>
        </div>
    );
};

SortableImage.propTypes = {
    id: PropTypes.string.isRequired,
    image: PropTypes.shape({
        urlImagen: PropTypes.string,
        name: PropTypes.string,
        clientKey: PropTypes.string,
    }).isRequired,
    pendiente: PropTypes.bool,
    onDelete: PropTypes.func.isRequired,
};

const SortableImageList = ({ items, onSortEnd, onDelete }) => {
    const sensors = useSensors(
        useSensor(PointerSensor, { activationConstraint: { distance: 5 } })
    );

    const ids = (items || []).map((img) => img.clientKey);

    const handleDragEnd = (event) => {
        const { active, over } = event;
        if (!over || active.id === over.id) return;
        const oldIndex = ids.indexOf(active.id);
        const newIndex = ids.indexOf(over.id);
        if (oldIndex === -1 || newIndex === -1) return;
        onSortEnd({ oldIndex, newIndex });
    };

    return (
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
            <SortableContext items={ids} strategy={rectSortingStrategy}>
                <div style={{ display: 'flex', flexWrap: 'wrap' }}>
                    {(items || []).map((image) => (
                        <SortableImage
                            key={image.clientKey}
                            id={image.clientKey}
                            image={image}
                            pendiente={esImagenSinProcesar(image)}
                            onDelete={() => onDelete(image.clientKey)}
                        />
                    ))}
                </div>
            </SortableContext>
        </DndContext>
    );
};

SortableImageList.propTypes = {
    items: PropTypes.array,
    onSortEnd: PropTypes.func.isRequired,
    onDelete: PropTypes.func.isRequired,
};

class ImageUploader extends Component {
    constructor(props) {
        super(props);
        this.state = {
            images: [],
        };
    }

    componentDidMount() {
        this.cargarImagenesIniciales(this.props.publicacion?.imagenes);
    }

    componentDidUpdate(prevProps) {
        if (this.imagenesEditadas) return;
        const actuales = this.props.publicacion?.imagenes;
        if (actuales !== prevProps.publicacion?.imagenes) {
            this.cargarImagenesIniciales(actuales);
        }
    }

    cargarImagenesIniciales = (imagenes) => {
        this.setState({ images: prepararImagenes(ordenarImagenes(imagenes)) });
    };

    publicarImagenes = (images) => {
        const preparadas = prepararImagenes(images);
        this.imagenesEditadas = true;
        this.setState({ images: preparadas });
        this.props.setImagesUploader(preparadas, true);
    };

    handleImageUpload = (event) => {
        const files = Array.from(event.target.files);
        const image = event.target.files[0];

        if (image.size > 512000) {
            toast.error("Tamaño de imagen debe ser menor a 500kb");
            return;
        }

        const newImages = files.map((file) => ({
            urlImagen: URL.createObjectURL(file),
            name: file.name,
            imagen: file,
            clientKey: `nueva-${file.name}-${file.size}-${file.lastModified}-${Math.random().toString(36).slice(2, 8)}`,
        }));
        this.publicarImagenes([...(this.state.images || []), ...newImages]);
    };

    onSortEnd = ({ oldIndex, newIndex }) => {
        this.publicarImagenes(arrayMove(this.state.images || [], oldIndex, newIndex));
    };

    handleDeleteImage = (clientKey) => {
        const imageToDelete = (this.state.images || []).find((image) => image.clientKey === clientKey);
        if (imageToDelete?.urlImagen?.startsWith('blob:')) {
            URL.revokeObjectURL(imageToDelete.urlImagen);
        }
        this.publicarImagenes((this.state.images || []).filter((image) => image.clientKey !== clientKey));
    };

    render() {
        const { images } = this.state;

        return (
            <div>
                <input
                    type="file"
                    multiple
                    accept="image/*"
                    onChange={this.handleImageUpload}
                    style={{ marginBottom: '20px' }}
                />
                <SortableImageList
                    items={images}
                    onSortEnd={this.onSortEnd}
                    onDelete={this.handleDeleteImage}
                />
            </div>
        );
    }
}

ImageUploader.propTypes = {
    publicacion: PropTypes.shape({
        imagenes: PropTypes.array,
        idPublicacion: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    }),
    setImagesUploader: PropTypes.func,
};

const mapStateToProps = (state) => ({
    publicacion: state.publicaciones.publicacion,
    userId: state.general.userId,
    imagesUploader: state.publicaciones.imagesUploader,
});

export default connect(mapStateToProps, { fetchPublicacion, editPublicacion, setImagesUploader })(ImageUploader);
